import {
  ArrowDownToLineIcon,
  ArrowUpToLineIcon,
  CloseIcon,
  IconButton,
  Text,
  Tooltip,
} from "@carbon6/c6-frontend-design";
import { appTextModes } from "@/shared/ui/text";
import { useLayoutEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useWindowTaskbarPositionPreference } from "./taskbarPosition";
import type { WindowEntry } from "./WindowStack";
import { WINDOW_TASKBAR_Z } from "./windowLayers";
import styles from "./WindowTaskbar.module.css";

type WindowTaskbarProps = {
  // 떠 있는 모든 전체화면 창(활성+최소화). 작업표시줄은 전부 나열한다.
  entries: readonly WindowEntry[];
  // 지금 전체화면으로 보이는 창. 그 항목을 강조(눌린 상태)한다. null 이면 모두 배경(최소화).
  activeId: string | null;
  // 본문 클릭 → 호스트가 분기한다(활성 항목이면 최소화로 토글, 아니면 그 창을 전체화면으로 전환).
  onSelect: (id: string) => void;
  // [X] → 그 창을 닫는다.
  onClose: (id: string) => void;
  ariaLabel?: string;
};

function renderTaskbarLabel(entry: WindowEntry): ReactNode {
  return (
    entry.label ?? (
      <Text variant={appTextModes["caption-bold"]} className={styles.labelText}>
        {entry.title}
      </Text>
    )
  );
}

// 떠 있는 전체화면 창들을 보여주는 전역 작업표시줄(화면 상단/하단 도킹 full-width 막대). OS 작업표시줄처럼
// 활성 창이 전체화면일 때도 항상 보이고(z-index 가 오버레이보다 높다), 각 항목 클릭으로 창을 전환·
// 토글하고 [X] 로 닫는다. 우측 끝 화살표 버튼으로 상단/하단 도킹을 전환한다(브라우저에 저장).
// 콘텐츠/스택 정책은 모르고 표시·위임만 한다.
//
// body 로 portal 하는 이유: 활성 창(FullscreenWindow)이 c6 modalStack 에 등록되면 스택이 body 직속
// 자식 중 z 가 낮은 것을 inert 처리한다. 막대가 앱 루트(#root) 안에 있으면 루트째 inert 돼 죽으므로,
// body 직속 + 창보다 높은 z(inline) 로 두어 "모달 위 비-Modal 오버레이 보존" 규칙을 탄다.
export function WindowTaskbar({
  entries,
  activeId,
  onSelect,
  onClose,
  ariaLabel = "열린 창",
}: WindowTaskbarProps): ReactNode {
  const [position, setPosition] = useWindowTaskbarPositionPreference();

  // 작업표시줄이 떠 있는 동안 body 에 표식을 둔다 — 전역 CSS 가 일반 페이지와 FullscreenWindow
  // overlay 를 도킹 방향(상단/하단) 에 맞춰 offset 만큼 민다. 항상 entries.length>0 일 때만 마운트된다.
  useLayoutEffect(() => {
    document.body.dataset.windowTaskbar = "";
    return () => {
      delete document.body.dataset.windowTaskbar;
    };
  }, []);

  useLayoutEffect(() => {
    document.body.dataset.windowTaskbarPosition = position;
    return () => {
      delete document.body.dataset.windowTaskbarPosition;
    };
  }, [position]);

  // 하단 도킹이면 호버 툴팁이 화면 밖(막대 아래)이 아니라 막대 위로 뜨게 방향을 뒤집는다.
  const tooltipPlacement = position === "bottom" ? "top" : "bottom";
  // 도킹 글리프는 누르면 막대가 옮겨갈 방향을 가리킨다(상단 도킹=아래 막대로, 하단 도킹=위 막대로) —
  // 툴팁 문구와 같은 방향이다. 현재 위치를 가리키면 버튼이 이미 아는 사실을 되풀이하면서 툴팁과 어긋난다.
  const dockToggleLabel =
    position === "top"
      ? "작업 표시줄을 아래로 변경할 수 있습니다."
      : "작업 표시줄을 위로 변경할 수 있습니다.";

  return createPortal(
    <section
      className={position === "bottom" ? `${styles.taskbar} ${styles.bottom}` : styles.taskbar}
      style={{ zIndex: WINDOW_TASKBAR_Z }}
      aria-label={ariaLabel}
    >
      {entries.map((entry) => (
        <div
          key={entry.id}
          className={entry.id === activeId ? [styles.item, styles.active].join(" ") : styles.item}
        >
          {/* 항목이 좁아 이름은 말줄임된다 — 호버 시 전체 데이터셋 이름을 툴팁으로 보여준다.
              c6 Tooltip Bubble 이 flex 라 긴 토큰이 max-width 를 뚫는다 — 콘텐츠를 감싸 버블 안에서 줄바꿈. */}
          <Tooltip
            content={<span className={styles.tooltipText}>{entry.title}</span>}
            placement={tooltipPlacement}
            className={styles.selectTooltip}
          >
            <button
              type="button"
              className={styles.selectArea}
              aria-label={entry.title}
              aria-pressed={entry.id === activeId}
              onClick={() => onSelect(entry.id)}
            >
              <span className={styles.labelClip}>{renderTaskbarLabel(entry)}</span>
            </button>
          </Tooltip>
          {/* closable=false(진행 중 업로드 등)면 [X] 를 숨긴다 — 완료/폐기 전 닫기 금지. */}
          {entry.closable ? (
            <IconButton
              aria-label="종료"
              title="창을 종료합니다."
              variant="ghost"
              className={styles.closeButton}
              tone="primary"
              size="sm"
              border={false}
              onClick={() => onClose(entry.id)}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </div>
      ))}
      {/* 우측 끝 도킹 토글 — margin-left:auto 로 항목들과 분리해 오른쪽에 붙인다. c6 Tooltip 은
          작업표시줄이 허용한 예외(HatTooltip 아님). */}
      <Tooltip
        content={<span className={styles.tooltipText}>{dockToggleLabel}</span>}
        placement={tooltipPlacement}
        className={styles.dockTooltip}
      >
        <IconButton
          aria-label={dockToggleLabel}
          variant="ghost"
          tone="primary"
          size="sm"
          border={false}
          className={styles.dockToggle}
          onClick={() => setPosition(position === "top" ? "bottom" : "top")}
        >
          {position === "top" ? <ArrowDownToLineIcon /> : <ArrowUpToLineIcon />}
        </IconButton>
      </Tooltip>
    </section>,
    document.body,
  );
}
